/**
 * assignee-selection.js — reading the crew out of an assignee picker.
 *
 * The picker (templates/components/_assignee_picker.html, driven by
 * controllers/esm/assignee_picker_controller.js) appears in BOTH places a visit
 * is created or edited: the calendar page's quick-schedule modal and the
 * detail-page schedule modal. Those two submit paths live in different bundles
 * (calendar.bundle.js and controllers.bundle.js), so the DOM contract for
 * reading the picker lives here — one definition, imported by both — rather
 * than being restated at each call site.
 *
 * Deliberately Stimulus-free so the calendar bundle can import it without
 * pulling the controller runtime in.
 *
 * The picker's DOM contract:
 *   [data-assignee-picker]            the root
 *   input[data-assignee-check]        one per member, value = user id
 *   [data-assignee-lead][data-user-id] the lead toggle on a member row,
 *                                      .is-lead when that member leads
 */

const PICKER = '[data-assignee-picker]';

/** The picker inside `scope` (default: the document), or null when absent. */
export function findAssigneePicker(scope) {
    return (scope || document).querySelector(PICKER);
}

/**
 * The crew chosen in `picker`, as the payload keys the API expects.
 *
 * Returns `{}` when there is no picker on the page at all — an absent picker
 * must not be read as "clear the crew", which is what `{assignee_ids: []}`
 * means to CalendarService.update.
 *
 * @param {Element|Document} [scope] where to look for the picker
 * @returns {{assignee_ids?: number[], lead_user_id?: number|null}}
 */
export function readAssigneeSelection(scope) {
    const picker = findAssigneePicker(scope);
    if (!picker) return {};

    const ids = Array.from(picker.querySelectorAll('input[data-assignee-check]'))
        .filter((box) => box.checked)
        .map((box) => parseInt(box.value, 10))
        .filter((id) => !Number.isNaN(id));

    const lead = picker.querySelector('[data-assignee-lead].is-lead');
    const leadId = lead ? parseInt(lead.dataset.userId, 10) : NaN;

    return {
        assignee_ids: ids,
        lead_user_id: ids.includes(leadId) ? leadId : null
    };
}

/**
 * A visit's crew in the shape seedAssigneePicker takes.
 *
 * Every panel that edits an existing visit seeds from this, so a save that did
 * not touch the crew sends it back unchanged; an unseeded picker would send
 * `assignee_ids: []` and remove everyone on the visit.
 *
 * @param {Array<{user_id: number, is_lead: boolean}>} [assignees]
 *        the visit's `extendedProps.assignees` (CalendarEvent.to_fullcalendar_dict)
 * @returns {{userIds: number[], leadUserId: number|null}}
 */
export function crewFromAssignees(assignees) {
    const crew = assignees || [];
    return {
        userIds: crew.map((a) => a.user_id),
        leadUserId: (crew.find((a) => a.is_lead) || {}).user_id || null
    };
}

/**
 * Seed the picker in `scope` with a crew — empty for a new visit, the visit's
 * existing crew when editing one.
 *
 * Dispatched on the picker element rather than the document because the
 * calendar page has three pickers in the DOM at once (quick-schedule, event
 * detail, and the detail-page modal that ships in base.html); a broadcast
 * would seed all three.
 *
 * @param {Element|Document} scope
 * @param {{userIds?: number[], leadUserId?: number|null}} [crew]
 */
export function seedAssigneePicker(scope, crew) {
    const picker = findAssigneePicker(scope);
    if (!picker) return;
    picker.dispatchEvent(new CustomEvent('assignee-picker:set', {
        detail: {
            userIds: (crew && crew.userIds) || [],
            leadUserId: (crew && crew.leadUserId) || null
        }
    }));
}
